import React from 'react';
import {
    Alert,
    Chip,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import SimilarityGraph from './SimilarityGraph';
import SimilarityRelationList from './SimilarityRelationList';
import {resolveEeiNavPath} from './similarityLabels';
import {useSession} from '../../../firebase/UserProvider';

export function findEeiSimilarityBlock(similarityData, eeiId) {
    if (!similarityData?.eeis || !eeiId) return null;
    return similarityData.eeis.find((b) => b.id === eeiId) || null;
}

function openEei(block) {
    const path = resolveEeiNavPath({
        type: 'EEI',
        id: block?.id,
        number: block?.eeiNumber || block?.number,
    });
    if (path) window.open(path, '_blank', 'noopener,noreferrer');
}

/**
 * Per-EEI similarity block under "Similar to EEIs of this ICR".
 */
function EeiSimilarityBlock({eeiBlock, onEdgeReviewed, currentUserId}) {
    if (!eeiBlock) return null;
    const number = eeiBlock.eeiNumber || eeiBlock.number || eeiBlock.id;
    const text = eeiBlock.text || '';

    return (
        <Stack
            spacing={0.75}
            sx={{
                pb: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {borderBottom: 0, pb: 0},
            }}
        >
            <Typography variant="body2" sx={{lineHeight: 1.6}}>
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => openEei(eeiBlock)}
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        mr: text ? 1 : 0,
                        verticalAlign: 'baseline',
                        cursor: 'pointer',
                    }}
                >
                    {number}
                </Link>
                {text}
            </Typography>
            <SimilarityRelationList
                edges={eeiBlock.edges}
                byRelation={eeiBlock.byRelation}
                dense
                onEdgeReviewed={onEdgeReviewed}
                currentUserId={currentUserId}
                subject={{type: 'EEI', id: eeiBlock.id}}
                showAddRelation
            />
        </Stack>
    );
}

/**
 * CM/admin Similarity section: graph and ICR-level relation lists.
 */
export default function IcrSimilaritySection({similarity}) {
    const {
        data,
        loading,
        error,
        running,
    } = similarity;
    const {user} = useSession();
    const eeiBlocks = data?.eeis || [];

    return (
        <Stack spacing={1.5}>
            {(running || data?.job?.status === 'failed') && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {running && (
                        <Chip label="Recomputing…" size="small" color="info" variant="outlined"/>
                    )}
                    {data?.job?.status === 'failed' && (
                        <Chip label="Last run failed" size="small" color="error" variant="outlined"/>
                    )}
                </Stack>
            )}

            {running && (
                <Alert severity="info" variant="outlined">
                    Similarity recomputing in background…
                </Alert>
            )}
            {error && (
                <Alert severity="warning" variant="outlined">{error}</Alert>
            )}
            {loading && !data && (
                <Typography variant="body2" color="text.secondary">Loading similarity…</Typography>
            )}

            {data && (
                <>
                    <SimilarityGraph payload={data} onEdgeReviewed={similarity?.reload}/>
                    <Typography variant="subtitle2" fontWeight={700}>Similar to this ICR</Typography>
                    <SimilarityRelationList
                        edges={data.edges}
                        byRelation={data.byRelation}
                        onEdgeReviewed={similarity?.reload}
                        currentUserId={user?.uid}
                        subject={{type: data.subjectType || 'ICR', id: data.subjectId}}
                        showAddRelation
                    />
                    <Stack spacing={1.5} sx={{pt: 2}}>
                        <Typography variant="subtitle2" fontWeight={700}>Similar to EEIs of this ICR</Typography>
                        {eeiBlocks.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No EEIs on this ICR
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {eeiBlocks.map((block) => (
                                    <EeiSimilarityBlock
                                        key={block.id}
                                        eeiBlock={block}
                                        onEdgeReviewed={similarity?.reload}
                                        currentUserId={user?.uid}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
        </Stack>
    );
}
